import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getBackendUrl } from '../config';

const ChatContext = createContext();

/**
 * Custom Hook zur einfachen Verwendung des Chat-Contexts.
 */
export const useChat = () => useContext(ChatContext);

/**
 * ChatProvider verwaltet den ungelesenen Status von Nachrichten und
 * stellt Hilfsfunktionen für die Chat-Kommunikation zur Verfügung.
 */
export const ChatProvider = ({ children }) => {
  const { token, user, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [chats, setChats] = useState([]);

  /**
   * Bereinigt eventuelle doppelte Anführungszeichen am Anfang und Ende einer Nachricht.
   */
  const cleanMessage = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/^"|"$/g, '');
  };

  /**
   * Ruft alle aktiven Chats ab und ermittelt die Anzahl der ungelesenen Nachrichten,
   * indem gesendete Zeitstempel mit den lokal gespeicherten "lastSeen"-Zeiten abgeglichen werden.
   */
  const fetchUnreadStatus = async () => {
    if (!isAuthenticated || !token || !user) return;

    try {
      const res = await fetch(`${getBackendUrl()}/chat`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const allChats = await res.json();
        setChats(allChats);

        const lastSeenMap = JSON.parse(localStorage.getItem(`lastSeen_${user.id}`) || '{}');
        let totalUnread = 0;

        allChats.forEach(chat => {
          const otherUserId = chat.andererNutzer.id;
          const lastSeenTime = lastSeenMap[otherUserId] || 0;
          
          if (chat.nachrichten && Array.isArray(chat.nachrichten)) {
            chat.nachrichten.forEach(msg => {
              const msgTime = new Date(msg.gesendetAm).getTime();
              if (msg.senderId !== user.id && msgTime > lastSeenTime) {
                totalUnread++;
              }
            });
          }
        });

        setUnreadCount(totalUnread);
      }
    } catch (err) {
      console.error("Fehler beim Pollen der Nachrichten:", err);
    }
  };

  /**
   * Markiert alle Nachrichten mit einem bestimmten Benutzer als gelesen.
   */
  const markAsRead = (otherUserId) => {
    if (!user) return;
    const lastSeenMap = JSON.parse(localStorage.getItem(`lastSeen_${user.id}`) || '{}');
    lastSeenMap[otherUserId] = Date.now();
    localStorage.setItem(`lastSeen_${user.id}`, JSON.stringify(lastSeenMap));
    fetchUnreadStatus();
  };

  /**
   * Startet das automatische Abrufen (Polling) der Nachrichten im Intervall (alle 15 Sekunden).
   */
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadStatus();
      const interval = setInterval(fetchUnreadStatus, 15000);
      return () => clearInterval(interval);
    } else {
      setUnreadCount(0);
      setChats([]);
    }
  }, [isAuthenticated, token]);

  return (
    <ChatContext.Provider value={{ 
      unreadCount, 
      chats, 
      markAsRead, 
      cleanMessage, 
      refreshUnread: fetchUnreadStatus 
    }}>
      {children}
    </ChatContext.Provider>
  );
};
